import React, { PureComponent } from 'react';
import { connect } from 'react-redux';
import PropTypes from 'prop-types';
import Slide from '@mui/material/Slide';
import * as dates from '@simplybenefits/sb-dates';
import {
    Tiles,
    Loader,
    MessageCard,
    Text,
    Modal,
    PlanDesign,
    PlanOverview,
    MasterApplication,
    CommissionsForm,
    Census,
    Services,
    DateInput,
    Pricing,
    ClassesDivisions,
    ReviewRates,
    LsaSelectionModal,
    ContainerControlSite,
    Content,
    SpecialityProductRates,
    Files,
    i18n,
    tokens
} from '@simplybenefits/sb-ui-library';
import config from '../../config.js';
import Header from '../../components/Header.js';
import AccountTerminationModal from './AccountTerminationModal.js';
import { listFiles, createFolder, uploadFile, downloadFile, removeFile, makePrivate, makePublic } from '../../actions/files.js';
import {
    getAccount,
    getAccountHistoryKeys,
    allowEnrollments,
    savePlanDesign,
    savePlanDesignBenefitInitData,
    savePlanDesignField,
    getPlanDesignRevisions,
    getSinglePlanDesignRevision,
    revertToPlanDesignRevision,
    saveModularBlueprint,
    saveModularBenefitInitData,
    saveModularField,
    getMasterApplicationData,
    updateMasterApp,
    completeMasterApp,
    submitCommissions,
    saveCommissions,
    getCommissions,
    addUser,
    updateUser,
    removeUser,
    addDivision,
    editDivision,
    editClass,
    addClass,
    uploadCensus,
    submitCensus,
    makeAccountLive,
    submitModuleBlueprint,
    submitPlanDesigns,
    createPlanDesign,
    createPlanDesignFromUpload,
    clonePlanDesign,
    submitPricing,
    savePricingInput,
    savePricing,
    saveTelusDrugPlanID,
    getPricingRevisions,
    getSinglePricingRevision,
    sendMasterAppLink,
    getLsaItems,
    saveLsaItems,
    getSpecialityProductMetaConfigs, 
    getSpecialityProductRates, 
    saveSpecialityProductRates,
    getCommissionSplits,
    replaceCommissionSplits
} from '../../actions/accounts.js';

import * as notifier from '../../actions/notifier.js';
import Members from './Members/index.js';
import Administrators from './Administrators/index.js';
import Booklets from './Booklets/index.js';
import Settings from './Settings.js';
import AsoReports from './AsoReports/index.js';
import AccountReports from './AccountReports/index.js';
import Notifications from '../Notifications/NotificationsList.js';
import Invoices from './Invoices.js';
import CarryForward from '../CarryForward/CarryForward.js';
import AccountNotes from './AccountNotes/index.js';
import PendingChanges from './PendingChanges/index.js';
import Representatives from './Representatives/index.js';
import permissionLevel from '../../services/permissions.js';
import PermissionsComponent from '../../components/PermissionsComponent.js';
import { getPdf } from '../../actions/master.js';
import { validateBankingDetails, getAdvisor, searchAdvisors } from '../../actions/advisors.js';

class AccountDashboard extends PureComponent {
    state = {
        selectedSection: null,
        settingsModalOpen: false,
        showAllowEnrollmentsDialog: false,
        showGoLiveDialog: false,
        showAccountTerminationModal: false,
        showLsaSelections: false,
        effDate: dates.getUTC()
    };

    _transition = React.forwardRef((props, ref) => <Slide ref={ref} direction="up" {...props} />);

    static getDerivedStateFromProps(props) {
        if (!permissionLevel('Accounts', props.accessType).includes('read')) {
            window.location.href = '/403';
        }

        return null;
    }

    async componentDidMount() {
        await this.props.dispatch(getAccount(this.props.match.params.accountID));

        if (!this.props.advisors.currentAdvisor) {
            this.props.dispatch(getAdvisor(this.props.currentAccountMeta.advisorID));
        }
        
        const data = await this.props.dispatch(getCommissionSplits(this.props.match.params.accountID));

        this.setState({
            commissionSplits: data
        });

        // set the default effDate to be the start of the next month
        const now = new Date();
        const effDate = new Date(now.getFullYear(), now.getMonth() + 1, 1);

        if (window.location.hash && window.location.hash !== '#dashboard') {
            this.setState({
                effDate: Services.dates.formatIncomingDate(Math.floor(effDate.getTime() / 1000)),
                selectedSection: window.location.hash.replace('#', '')
            });
        } else {
            this.setState({
                effDate: Services.dates.formatIncomingDate(Math.floor(effDate.getTime() / 1000))
            });
        }
    }

    _selectSection = (section) => {
        if (section) {
            window.location.hash = section;
        } else {
            window.location.hash = 'dashboard';
        }

        this.setState({
            selectedSection: section
        });
    };

    _toggleSettingsModal = () => {
        this.setState({
            settingsModalOpen: !this.state.settingsModalOpen
        });
    };

    _toggleGoLiveDialog = () => {
        this.setState({
            showGoLiveDialog: !this.state.showGoLiveDialog
        });
    };

    _toggleAccountTerminationModal = () => {
        this.setState({
            showAccountTerminationModal: !this.state.showAccountTerminationModal
        });
    };

    _enableEnrollments = async () => {
        const success = await this.props.dispatch(allowEnrollments(this.props.currentAccountMeta.accountID));

        if (success) {
            this.setState({
                showAllowEnrollmentsDialog: false
            });
        }
    };

    _navigateTo = (link) => {
        window.location.href = link;
    };

    _buildBreadcrumbs = () => {
        if (!this.state.selectedSection) {
            return [
                {
                    title: 'Accounts',
                    link: `/accounts`
                }
            ];
        }

        return [
            {
                title: 'Accounts',
                link: `/accounts`
            },
            {
                title: this.props.currentAccountMeta.company,
                onClick: () => this._selectSection(null)
            }
        ];
    };

    downloadPlanDesign = () => {
        this.props.dispatch(getPdf("Plan", i18n.t(tokens.labels.planDesignFileName), { accountID: this.props.currentAccountMeta.accountID }));
    };

    _getSubHeader = () => {
        if (this.state.selectedSection === 'account-employees') {
            return 'Members';
        } else if (this.state.selectedSection === 'account-admins') {
            return 'Administrators';
        } else if (this.state.selectedSection === 'account-rates-summary') {
            return 'Rates Summary';
        } else if (this.state.selectedSection === 'account-settings') {
            return 'Settings';
        } else if (this.state.selectedSection === 'account-plan') {
            return 'Plan';
        } else if (this.state.selectedSection === 'account-master-application') {
            return 'Master Application';
        } else if (this.state.selectedSection === 'account-notifications') {
            return 'Notifications';
        } else if (this.state.selectedSection === 'account-plan-pricing') {
            return 'Plan Pricing';
        } else if (this.state.selectedSection === 'account-plan-design') {
            return 'Plan Designs';
        } else if (this.state.selectedSection === 'account-plan-overview') {
            return 'Plan Overview';
        } else if (this.state.selectedSection === 'account-invoices') {
            return 'Invoices';
        } else if (this.state.selectedSection === 'account-member-booklets') {
            return 'Member Booklets';
        } else if (this.state.selectedSection === 'account-census') {
            return 'Census';
        } else if (this.state.selectedSection === 'commissions') {
            return 'Commissions';
        } else if (this.state.selectedSection === 'account-history') {
            return 'Account History';
        } else if (this.state.selectedSection === 'request-approval') {
            return 'Request Approval';
        } else if (this.state.selectedSection === 'classes-divisions') {
            return 'Classes and Divisions';
        } else if (this.state.selectedSection === 'account-reports') {
            return 'Reports';
        } else if (this.state.selectedSection === 'account-renewals') {
            return 'Renewals';
        } else if (this.state.selectedSection === 'account-pending-changes') {
            return 'Pending Changes';
        } else if (this.state.selectedSection === 'terminate-account') {
            return 'Pending Changes';
        } else if (this.state.selectedSection === 'account-division-carry-forward') {
            return 'Division Carry Forwards';
        } else if (this.state.selectedSection === 'account-notes') {
            return 'Notes';
        } else if (this.state.selectedSection === 'speciality-product-rates') {
            return "Speciality Product Rates";
        } else if (this.state.selectedSection === 'account-files') {
            return 'Files';
        } else if (this.state.selectedSection === 'account-representatives') {
            return 'Representatives';
        }

        return this.props.currentAccountMeta.company;
    };

    _downloadAccountHistory = async () => {
        const keys = await this.props.dispatch(getAccountHistoryKeys(this.props.currentAccountMeta.accountID));

        keys.map((key) => {
            const url = `${ config.apiRequestUrl }/accounts/${ this.props.currentAccountMeta.accountID }/history/file/${ key.type }/${ key.filename }`;
            window.open(encodeURI(url));
            return true;
        });
    };

    _setEffectiveDate = (e) => {
        this.setState({
            effDate: e.target.value
        });
    };

    _goLive = async () => {
        // validate the effective date is correct
        const now = new Date();
        const tempDate = new Date(now.getFullYear(), now.getMonth(), 1);
        const firstOfThisMonth = Services.dates.formatIncomingDate(Math.floor(tempDate.getTime() / 1000));
        let error = false;

        // Effective date can be first of current month
        if (firstOfThisMonth !== this.state.effDate) {
            error = await Services.validateInput('effDate', this.state.effDate);
        }

        if (error) {
            this.setState({
                effectiveDateError: error
            });

            return false;
        }

        const temp = this.state.effDate.split('/');
        const day = temp[2];

        if (day !== '01') {
            error = 'The effective date must start on the 1st of the month.';
        }

        this.setState({
            effectiveDateError: error
        });

        if (error) {
            return false;
        }

        this._toggleGoLiveDialog();
        this.props.dispatch(makeAccountLive(this.props.match.params.accountID, Services.dates.formatOutgoingDate(this.state.effDate)));
        return null;
    };

    _downloadPricingPdf = async (ComponentName, fileName, params) => {
        await this.props.dispatch(getPdf(ComponentName, fileName, params));
    };

    _checkIfFutureDate = (date) => {
        const dateForDisplay = dates.getUTC(date, "YYYY/MM/DD");
        const isDateFutureDated = dates.validate(dateForDisplay);

        // this reuses the date validation function to check if the date is in the future
        if (isDateFutureDated === 'Date cannot be in the future.') {
            return true;
        }

        return false;
    };

    _searchAdvisors = (query) => this.props.dispatch(searchAdvisors(query));

    _replaceCommissionSplits = async (...params) => {
        const updatedCommissionSplitData = await this.props.dispatch(replaceCommissionSplits(this.props.currentAccountMeta.accountID, ...params));

        if (updatedCommissionSplitData) {
            this.setState({
                commissionSplits: updatedCommissionSplitData
            });

            return true;
        }

        return false;
    };

    render() {
        if (!this.props.currentAccountMeta) {
            return <Loader/>;
        }

        const { currentAccountMeta, accessType, notifications } = this.props;
        const showBlockedClaimsMessage = currentAccountMeta.accountClaimsBlocked || currentAccountMeta.divisionsClaimsBlocked;
        const claimsBlockedFor = showBlockedClaimsMessage && currentAccountMeta.accountClaimsBlocked ? i18n.t(tokens.labels.account.allAccountDivisions) : currentAccountMeta.blockedDivisionTitles;
        
        const subHeader = this._getSubHeader();
        const breadcrumbs = this._buildBreadcrumbs();
        let tiles = [];

        const tileOptions = {
            accountDetails: (step = false, completed = false) => ({
                title: 'Account Details',
                description: 'View and manage account details.',
                onClick: () => this._toggleSettingsModal(),
                testid: 'account-settings-tile',
                step,
                completed
            }),
            amendments: (step = false, completed = false) => ({
                title: 'Amendments',
                description: 'View and manage policy amendments.',
                link: `/accounts/${ currentAccountMeta.accountID }/amendments`,
                step,
                completed
            }),
            census: (step = false, completed = false) => ({
                title: 'Census',
                description: 'Input and manage member information in bulk.',
                onClick: () => this._selectSection('account-census'),
                testid: 'account-employees-tile',
                step,
                completed
            }),
            employees: (step = false, completed = false) => ({
                title: 'Members',
                description: 'View and manage members.',
                onClick: () => this._selectSection('account-employees'),
                testid: 'account-employees-tile',
                step,
                completed
            }),
            classesDivisions: (step = false, completed = false) => ({
                title: 'Classes & Divisions',
                description: 'Manage the Classes and Divisions for the Quote.',
                onClick: () => this._selectSection('classes-divisions'),
                testid: 'classes-divisions-tile',
                step,
                completed
            }),
            planDesigns: (step = false, completed = false) => ({
                title: 'Plan Designs',
                description: 'Review the plan designs.',
                onClick: () => this._selectSection('account-plan-design'),
                testid: 'account-plan-design-tile',
                step,
                completed
            }),
            planOverview: (step = false, completed = false) => ({
                title: 'Plan Overview',
                description: 'Review the plan overview.',
                onClick: () => this._selectSection('account-plan-overview'),
                testid: 'account-plan-overview-tile',
                step,
                completed
            }),
            commissions: (step = false, completed = false) => ({
                title: 'Commissions',
                description: 'Review and set commissions.',
                onClick: () => this._selectSection('commissions'),
                testid: 'account-commissions-tile',
                step,
                completed
            }),
            planPricing: (step = false, completed = false) => ({
                title: 'Plan Pricing',
                description: 'Review and set pricing.',
                onClick: () => this._selectSection('account-plan-pricing'),
                testid: 'account-plan-pricing-tile',
                step,
                completed
            }),
            reviewRates: (step = false, completed = false) => ({
                title: 'Review Rates',
                description: 'View summary of rates per class.',
                onClick: () => this._selectSection('account-rates-summary'),
                testid: 'account-rates-summary-tile',
                step,
                completed
            }),
            invoices: (step = false, completed = false) => ({
                title: 'Invoices',
                description: 'Click to view invoices.',
                onClick: () => this._selectSection('account-invoices'),
                testid: 'account-invoices',
                step,
                completed
            }),
            accountHistory: (step = false, completed = false) => ({
                title: 'Account History',
                description: 'Download the account history documents.',
                onClick: this._downloadAccountHistory,
                testid: 'account-history-tile',
                step,
                completed
            }),
            masterApplication: (step = false, completed = false) => ({
                title: 'Master Application',
                description: 'View the Master Application',
                onClick: () => this._selectSection('account-master-application'),
                testid: 'account-master-application-tile',
                step,
                completed
            }),
            allowEnrollments: (step = false, completed = false) => ({
                title: 'Allow Enrollments',
                description: 'Click to allow enrollments to be sent.',
                onClick: () => this.setState({ showAllowEnrollmentsDialog: true }),
                testid: 'account-allow-enrollments-tile',
                step,
                completed
            }),
            administrators: (step = false, completed = false) => ({
                title: 'Administrators',
                description: 'View and manage team administrators.',
                onClick: () => this._selectSection('account-admins'),
                testid: 'account-administrators-tile',
                step,
                completed
            }),
            memberBooklets: (step = false, completed = false) => ({
                title: 'Member Booklets',
                description: 'Review and upload member booklets.',
                onClick: () => this._selectSection('account-member-booklets'),
                testid: 'member-booklets-tile',
                step,
                completed
            }),
            reports: (step = false, completed = false) => ({
                title: 'Reports',
                description: 'View and download reports for this account.',
                onClick: () => this._selectSection('account-reports'),
                testid: 'account-reports-tile',
                step,
                completed
            }),
            renewals: (step = false, completed = false) => ({
                title: 'Renewals',
                description: 'View Account Renewals.',
                link: `/accounts/${ currentAccountMeta.accountID }/renewals`,
                testid: 'account-renewals-tile',
                step,
                completed
            }),
            pendingChanges: (step = false, completed = false) => ({
                title: 'Pending Changes',
                description: 'View all pending changes.',
                onClick: () => this._selectSection('account-pending-changes'),
                testid: 'account-pending-changes-tile',
                step,
                completed
            }),
            lsaCategories: (step = false, completed = false) => ({
                title: 'LSA Categories',
                description: 'View and set LSA categories.',
                onClick: () => this.setState({ showLsaSelections: true }),
                testid: 'account-renewals-tile',
                step,
                completed
            }),
            asoReporting: (step = false, completed = false) => ({
                title: 'ASO Reporting',
                description: 'View ASO Balances.',
                onClick: () => this._selectSection('account-aso-reporting'),
                testid: 'account-aso-reporting-tile',
                step,
                completed
            }),
            divisionCarryForwards: (step = false, completed = false) => ({
                title: 'Division Carry Forwards',
                description: "View the account's pending division carry forward.",
                onClick: () => this._selectSection('account-division-carry-forward'),
                testid: 'account-division-carry-forward-tile',
                icon: 'Carry Forward',
                step,
                completed
            }),
            representatives: (step = false, completed = false) => ({
                title: 'Representatives',
                description: 'View and assign representatives for this account.',
                onClick: () => this._selectSection('account-representatives'),
                testid: 'account-representatives-tile',
                step,
                completed
            }),
            terminateAccount: (step = false, completed = false) => ({
                title: 'Terminate Account',
                description: 'Terminate this account.',
                onClick: () => this._toggleAccountTerminationModal(),
                testid: 'account-termination-tile',
                step,
                completed
            }),
            accountNotes: (step = false, completed = false) => ({
                title: 'Account Notes',
                description: 'Manage account notes.',
                onClick: () => this._selectSection('account-notes'),
                testid: 'account-notes-tile',
                step,
                completed
            }),
            goLive: (step = false, completed = false) => ({
                title: 'Go Live',
                description: 'Set the account to active and start the benefits plan.',
                disabled: Number(this.props.currentAccountMeta.state) < 1,
                onClick: () => this._toggleGoLiveDialog(),
                testid: 'go-live-tile',
                step,
                completed
            }),
            specialityProductRates: (step = false, completed = false) => ({
                title: 'Speciality Product Rates',
                description: 'View and customize rates for your selected products.',
                onClick: () => this._selectSection('speciality-product-rates'),
                testid: 'speciality-product-rates-tile',
                step,
                completed
            }),
            files: (step = false, completed = false) => ({
                title: 'Files',
                description: 'Manage account files.',
                onClick: () => this._selectSection('account-files'),
                testid: 'account-files-tile',
                step,
                completed
            })
        };

        if (currentAccountMeta.status === 0) {
            if (currentAccountMeta.isPrivateLabel) {
                // for Pending Private Label Accounts (just after Quote)
                tiles = [
                    tileOptions.accountDetails(false, true),
                    tileOptions.administrators(false, true),
                    tileOptions.planDesigns(false, true),
                    tileOptions.specialityProductRates(false, true),
                    tileOptions.representatives(false, true),
                    tileOptions.census(5, Number(this.props.currentAccountMeta.state) >= 1),
                    tileOptions.goLive(6, Number(this.props.currentAccountMeta.state) >= 8)
                ];
            } else {
                // for Pending Standard Accounts (just after Quote)
                tiles = [
                    tileOptions.accountDetails(false, true),
                    tileOptions.planDesigns(false, true),
                    tileOptions.planPricing(false, true),
                    tileOptions.specialityProductRates(false, true),
                    tileOptions.commissions(false, true),
                    tileOptions.accountHistory(false, true),
                    tileOptions.administrators(false, true),
                    tileOptions.masterApplication(false, true),
                    tileOptions.memberBooklets(false, true),
                    tileOptions.representatives(false, true),
                    tileOptions.census(9, Number(this.props.currentAccountMeta.state) >= 1),
                    tileOptions.goLive(10, Number(this.props.currentAccountMeta.state) >= 8)
                ];
            }
        } else if (currentAccountMeta.isPrivateLabel) {
            // for live Private Label accounts
            permissionLevel('Account Settings', accessType).includes('read') &&
                tiles.push(tileOptions.accountDetails());

            permissionLevel('Administrators', accessType).includes('read') &&
                tiles.push(tileOptions.administrators());

            permissionLevel('Plan Designs', accessType).includes('read') &&
                tiles.push(tileOptions.planDesigns());

            permissionLevel('Plan Designs', accessType).includes('read') &&
                tiles.push(tileOptions.specialityProductRates());

            permissionLevel('Employees', accessType).includes('read') &&
                tiles.push(tileOptions.census());

            permissionLevel('Employees', accessType).includes('read') &&
                tiles.push(tileOptions.employees());

            permissionLevel('Employees', accessType).includes('read') &&
                tiles.push(tileOptions.classesDivisions());

            permissionLevel('Invoices', accessType).includes('read') &&
                tiles.push(tileOptions.invoices());

            permissionLevel('Reports', accessType).includes('read') &&
                tiles.push(tileOptions.reports());

            permissionLevel('Master Application', accessType).includes('read') &&
                tiles.push(tileOptions.masterApplication());

            permissionLevel('Renewals', accessType).includes('read') &&
                tiles.push(tileOptions.renewals());

            permissionLevel('Reports', accessType).includes('read') &&
                tiles.push(tileOptions.pendingChanges());

            permissionLevel('Account Notes', accessType).includes('read') &&
                tiles.push(tileOptions.accountNotes());

            permissionLevel('Account Files', accessType).includes('write') &&
                tiles.push(tileOptions.files());

            permissionLevel('Representatives', accessType).includes('read') &&
                tiles.push(tileOptions.representatives());

            permissionLevel('Allow Enrollments', accessType).includes('write') && this.props.currentAccountMeta.blockEnrollments === 1 &&
                tiles.push(tileOptions.allowEnrollments());

            permissionLevel('Terminate Accounts', accessType).includes('write') &&
                tiles.push(tileOptions.terminateAccount());
        } else {
            // if the account is live show the below tiles else show the setup tiles
            permissionLevel('Account Settings', accessType).includes('read') &&
                tiles.push(tileOptions.accountDetails());

            permissionLevel('Plan Designs', accessType).includes('read') &&
                tiles.push(tileOptions.amendments());

            permissionLevel('Employees', accessType).includes('read') &&
                tiles.push(tileOptions.census());

            permissionLevel('Employees', accessType).includes('read') &&
                tiles.push(tileOptions.employees());

            permissionLevel('Employees', accessType).includes('read') &&
                tiles.push(tileOptions.classesDivisions());

            permissionLevel('Plan Designs', accessType).includes('read') &&
                tiles.push(tileOptions.planDesigns());

            permissionLevel('Plan Overview', accessType).includes('read') &&
                tiles.push(tileOptions.planOverview());

            permissionLevel('Plan Pricing', accessType).includes('read') &&
                tiles.push(tileOptions.commissions());

            permissionLevel('Plan Pricing', accessType).includes('read') &&
                tiles.push(tileOptions.planPricing());

            permissionLevel('Plan Pricing', accessType).includes('read') &&
                tiles.push(tileOptions.specialityProductRates());

            permissionLevel('Review Rates', accessType).includes('read') &&
                tiles.push(tileOptions.reviewRates());

            permissionLevel('Invoices', accessType).includes('read') &&
                tiles.push(tileOptions.invoices());

            permissionLevel('Account History', accessType).includes('read') &&
                tiles.push(tileOptions.accountHistory());

            permissionLevel('Master Application', accessType).includes('read') &&
                tiles.push(tileOptions.masterApplication());

            permissionLevel('Administrators', accessType).includes('read') &&
                tiles.push(tileOptions.administrators());

            permissionLevel('Member Booklets', accessType).includes('read') &&
                tiles.push(tileOptions.memberBooklets());

            permissionLevel('Reports', accessType).includes('read') &&
                tiles.push(tileOptions.reports());

            permissionLevel('Renewals', accessType).includes('read') &&
                tiles.push(tileOptions.renewals());

            permissionLevel('Reports', accessType).includes('read') &&
                tiles.push(tileOptions.pendingChanges());

            if (this.props.currentAccountMeta.hasSpendingAccounts) {
                permissionLevel('Plan Designs', accessType).includes('read') &&
                    tiles.push(tileOptions.lsaCategories());
            }

            if (this.props.currentAccountMeta.asoType === 'Budgeted' && (this.props.currentAccountMeta.isAsoDental === 1 || this.props.currentAccountMeta.isAsoHealth === 1)) {
                permissionLevel('Reports', accessType).includes('read') &&
                    tiles.push(tileOptions.asoReporting());
            }

            permissionLevel('Invoices', accessType).includes('read') &&
                tiles.push(tileOptions.divisionCarryForwards());

            permissionLevel('Account Notes', accessType).includes('read') &&
                tiles.push(tileOptions.accountNotes());

            permissionLevel('Account Files', accessType).includes('write') &&
                tiles.push(tileOptions.files());

            permissionLevel('Representatives', accessType).includes('read') &&
                tiles.push(tileOptions.representatives());

            permissionLevel('Allow Enrollments', accessType).includes('write') && this.props.currentAccountMeta.blockEnrollments === 1 &&
                tiles.push(tileOptions.allowEnrollments());

            permissionLevel('Terminate Accounts', accessType).includes('write') &&
                tiles.push(tileOptions.terminateAccount());
        }

        const accountID = currentAccountMeta.accountID;
        const hasCommissionSplitWritePermissions = permissionLevel('Plan Pricing', accessType).includes('write');

        return (
            <ContainerControlSite background={"true"}>
                <Header
                    subHeader={subHeader}
                    breadcrumbs={breadcrumbs}
                    selectedTitle="accounts"
                />

                <Content>
                    <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                        <Text bold>
                            {currentAccountMeta.company} ({currentAccountMeta.groupNum})
                        </Text>

                        {
                            currentAccountMeta.isStudentGroup ?
                                <Text light>
                                    Student Group
                                </Text>
                                :
                                null
                        }

                        <Text
                            light
                            onClick={() => this._navigateTo(`/advisors/${ this.props.currentAccountMeta.advisorID }`)}
                            customStyle={{ cursor: 'pointer' }}
                        >
                            Advisor: {currentAccountMeta.advisor}
                        </Text>

                        <Text
                            light
                            customStyle={{ cursor: 'pointer' }}
                        >
                            Brokerage: {currentAccountMeta.brokerageName}
                        </Text>

                        {
                            currentAccountMeta.effDate &&
                                <Text light>
                                    Effective Date: {dates.getStartOfDay(currentAccountMeta.effDate, 'll')}
                                </Text>
                        }

                        <Text light>
                            Secondary Policy #: {currentAccountMeta.secondaryPolicyNumber || 'None'}
                        </Text>

                        {
                            this.props.representatives.assigned.length > 0 &&
                            <>
                                {
                                    this.props.representatives.assigned.map((item) => (
                                        <Text light>{ `${ item.type }: ${ item.name }` }</Text>
                                    ))
                                }
                            </>
                        }
                    </div>

                    {
                        !this.state.selectedSection &&
                        <React.Fragment>
                            {
                                showBlockedClaimsMessage
                                    ? (
                                        <MessageCard
                                            title={i18n.t(tokens.titles.claimsBlocked)}
                                            subtitle={i18n.t(tokens.labels.account.claimsBlockedAccount, { claimsBlockedFor })}
                                            isError={true}
                                        />
                                    )
                                    : null
                            }
                            {
                                this.props.currentAccountMeta.status === 0 &&
                                <MessageCard
                                    title="Account Pending"
                                    subtitle="This account has been Sold but not yet put live. Once the advisor has set the Census data and reviewed all the changes they can set the account live. If you need to make any changes you can do so below."
                                />
                            }

                            {
                                this.props.currentAccountMeta.terminationDate !== null && this._checkIfFutureDate(this.props.currentAccountMeta.terminationDate) ?
                                    <MessageCard
                                        title="Account To Be Terminated"
                                        subtitle={`This account will be terminated on ${ dates.getUTC(this.props.currentAccountMeta.terminationDate, "ll") }. The last effective date of coverage will be  ${ dates.getPastDate(this.props.currentAccountMeta.terminationDate, 1, "days", "ll") }.`}
                                        isError={true}
                                    />
                                    :
                                    null
                            }

                            <Tiles tiles={tiles} stepped={currentAccountMeta.status !== 1}/>
                        </React.Fragment>
                    }

                    {
                        this.state.selectedSection === 'account-census' &&
                        <Census
                            setSelectedSection={this._selectSection}
                            accountID={accountID}
                            currentAccountMeta={this.props.currentAccountMeta}
                            classData={this.props.classData}
                            divisions={this.props.divisions}
                            census={this.props.census}
                            displayError={(...params) => this.props.dispatch(notifier.displayError(...params))}
                            uploadCensus={(...params) => this.props.dispatch(uploadCensus(...params))}
                            submitCensus={(...params) => this.props.dispatch(submitCensus(...params))}
                            addUser={(...params) => this.props.dispatch(addUser(...params))}
                            updateUser={(...params) => this.props.dispatch(updateUser(...params))}
                            removeUser={(...params) => this.props.dispatch(removeUser(...params))}
                            addDivision={(...params) => this.props.dispatch(addDivision(...params))}
                            editDivision={(...params) => this.props.dispatch(editDivision(...params))}
                            editClass={(...params) => this.props.dispatch(editClass(...params))}
                            addClass={(...params) => this.props.dispatch(addClass(...params))}
                            isMasterSite={true}
                        />
                    }

                    {
                        this.state.selectedSection === 'classes-divisions' &&
                            <ClassesDivisions
                                currentAccountMeta={this.props.currentAccountMeta}
                                setState={this.props.setState}
                                step={this.props.currentAccountMeta.state}
                                addDivision={(...params) => this.props.dispatch(addDivision(...params))}
                                editDivision={(...params) => this.props.dispatch(editDivision(...params))}
                                editClass={(...params) => this.props.dispatch(editClass(...params))}
                                addClass={(...params) => this.props.dispatch(addClass(...params))}
                                classData={this.props.classData}
                                divisions={this.props.divisions}
                                isQuote={false}
                                isMasterSite={true}
                                loader={ this.props.loader }
                                validateBankingDetails={ (...params) => this.props.dispatch(validateBankingDetails(...params)) }
                                bankingDetailsVerifying={ this.props.bankingDetailsVerifying }
                            />
                    }

                    {
                        this.state.selectedSection === 'account-employees' &&
                        <Members accountID={accountID}/>
                    }

                    {
                        this.state.selectedSection === 'account-admins' &&
                        <Administrators accountID={accountID}/>
                    }

                    {
                        this.state.selectedSection === 'account-representatives' &&
                            <Representatives accountID={accountID}/>
                    }

                    {
                        this.state.selectedSection === 'account-rates-summary' &&
                        <ReviewRates
                            classData={this.props.classData}
                            designs={this.props.designs}
                            setSelectedSection={this._setSelectedSection}
                            isMasterSite={true}
                            currentAccountMeta={this.props.currentAccountMeta}
                            downloadPricingPdf={this._downloadPricingPdf}
                        />
                    }

                    {
                        this.state.selectedSection === 'account-plan-pricing' &&
                        <Pricing
                            isMaserSite={true}
                            setSelectedSection={this._selectSection}
                            accountID={accountID}
                            classData={this.props.classData}
                            currentAccountMeta={this.props.currentAccountMeta}
                            designs={this.props.designs}
                            commissions={this.props.commissions}
                            submitPricing={() => this.props.dispatch(submitPricing(accountID))}
                            savePricingInput={(...params) => this.props.dispatch(savePricingInput(...params))}
                            savePricing={(...params) => this.props.dispatch(savePricing(...params))}
                            saveTelusDrugPlanID={(...params) => this.props.dispatch(saveTelusDrugPlanID(...params))}
                            getPricingRevisions={(...params) => this.props.dispatch(getPricingRevisions(...params))}
                            getSinglePricingRevision={(...params) => this.props.dispatch(getSinglePricingRevision(...params))}
                            loader={ this.props.loader }
                        />
                    }

                    {
                        this.state.selectedSection === 'account-plan-design' &&
                        <PlanDesign
                            isMasterSite={true}
                            isQuote={false}
                            setSelectedSection={this._selectSection}
                            step={this.props.currentAccountMeta.state}
                            currentAccountMeta={this.props.currentAccountMeta}
                            classData={this.props.classData}
                            designs={this.props.designs}
                            modularBlueprints={this.props.modularBlueprints}
                            createPlanDesign={(...params) => this.props.dispatch(createPlanDesign(...params))}
                            createPlanDesignFromUpload={(...params) => this.props.dispatch(createPlanDesignFromUpload(...params))}
                            clonePlanDesign={(...params) => this.props.dispatch(clonePlanDesign(...params))}
                            savePlanDesign={(...params) => this.props.dispatch(savePlanDesign(...params))}
                            savePlanDesignBenefitInitData={(...params) => this.props.dispatch(savePlanDesignBenefitInitData(...params))}
                            savePlanDesignField={(...params) => this.props.dispatch(savePlanDesignField(...params))}
                            getPlanDesignRevisions={(...params) => this.props.dispatch(getPlanDesignRevisions(...params))}
                            getSinglePlanDesignRevision={(...params) => this.props.dispatch(getSinglePlanDesignRevision(...params))}
                            revertToRevision={(...params) => this.props.dispatch(revertToPlanDesignRevision(...params))}
                            saveModularBenefitInitData={(...params) => this.props.dispatch(saveModularBenefitInitData(...params))}
                            saveModularField={(...params) => this.props.dispatch(saveModularField(...params))}
                            saveModularBlueprint={(...params) => this.props.dispatch(saveModularBlueprint(...params))}
                            submitPlanDesigns={(...params) => this.props.dispatch(submitPlanDesigns(...params))}
                            submitModuleBlueprint={(...params) => this.props.dispatch(submitModuleBlueprint(...params))}
                            getSpecialityProductMetaConfigs={() => this.props.dispatch(getSpecialityProductMetaConfigs(this.props.currentAccountMeta.advisorID))}
                            specialityProductMetaConfigs={this.props.specialityProductMetaConfigs}
                            isPrivateLabelAdvisor={Boolean(this.props.currentAccountMeta.privateLabelID)}
                            loading={this.props.loading}
                        />
                    }

                    {
                        this.state.selectedSection === 'speciality-product-rates' &&
                            <SpecialityProductRates
                                isMasterSite={ false }
                                isAccountLive={ true }
                                specialityProductMetaConfigs={ this.props.specialityProductMetaConfigs }
                                getSpecialityProductRates={ () => this.props.dispatch(getSpecialityProductRates(this.props.currentAccountMeta.accountID, this.props.currentAccountMeta.advisorID, null))}
                                saveSpecialityProductRates={ (...params) => this.props.dispatch(saveSpecialityProductRates(this.props.currentAccountMeta.accountID, null, ...params))}
                                currentAccountMeta={ this.props.currentAccountMeta }
                                loader={ this.props.loader }
                                whitelabel={{}}
                                downloadRatesPdf={ (...params) => this.props.dispatch(getPdf(...params)) }
                            />
                    }

                    {
                        this.state.selectedSection === 'account-plan-overview' &&
                            <PlanOverview
                                meta={ this.props.currentAccountMeta }
                                whitelabel={{}}
                                plan={ this.props.plan }
                                downloadPlanDesign={ this.downloadPlanDesign }
                            />
                    }

                    {
                        this.state.selectedSection === 'commissions' &&
                        <CommissionsForm
                            isMasterSite={true}
                            isQuote={false}
                            isRevision={false}
                            currentAccountMeta={this.props.currentAccountMeta}
                            setSelectedSection={this._selectSection}
                            submitCommissions={(...params) => this.props.dispatch(submitCommissions(...params))}
                            saveCommissions={(...params) => this.props.dispatch(saveCommissions(...params))}
                            getCommissions={(...params) => this.props.dispatch(getCommissions(...params))}
                            loader={ this.props.loader }
                            searchAdvisors={this._searchAdvisors}
                            currentAdvisor={this.props.advisors.currentAdvisor}
                            commissionSplits={this.state.commissionSplits}
                            replaceCommissionSplits={this._replaceCommissionSplits}
                            hasCommissionSplitWritePermissions={hasCommissionSplitWritePermissions}
                        />
                    }

                    {
                        this.state.selectedSection === 'account-history' &&
                            <Files
                                isMasterSite={ true }
                                type="accounts"
                                id={ this.props.currentAccountMeta.accountID }
                                parentFolder="Account History"
                                dispatch={ this.props.dispatch }
                                listFiles={ listFiles }
                                createFolder={ createFolder }
                                uploadFile={ uploadFile }
                                downloadFile={ downloadFile }
                                removeFile={ removeFile }
                                makePrivate={ makePrivate }
                                makePublic={ makePublic }
                                loader={this.props.loader}
                            />
                    }

                    {
                        this.state.selectedSection === 'account-master-application' &&
                        <MasterApplication
                            whitelabel={{}}
                            isMasterSite={true}
                            currentAccountMeta={this.props.currentAccountMeta}
                            displayError={(...params) => this.props.dispatch(notifier.displayError(...params))}
                            setSelectedSection={this._selectSection}
                            isReadOnly={this.props.currentAccountMeta.status !== 3} // if its not in quoting or in progress (status = 3)
                            getMasterApplicationData={(...params) => this.props.dispatch(getMasterApplicationData(...params))}
                            updateMasterApp={(...params) => this.props.dispatch(updateMasterApp(...params))}
                            completeMasterApp={(...params) => this.props.dispatch(completeMasterApp(...params))}
                            loader={ this.props.loader }
                        />
                    }

                    {
                        this.state.selectedSection === 'request-approval' &&
                        <MasterApplication
                            setSelectedSection={this._selectSection}
                            isMasterSite={this.props.isMasterSite}
                            isQuote={false}
                            currentAccountMeta={this.props.currentAccountMeta}
                            getMasterApplicationData={(...params) => this.props.dispatch(getMasterApplicationData(...params))}
                            updateMasterApp={(...params) => this.props.dispatch(updateMasterApp(...params))}
                            completeMasterApp={(...params) => this.props.dispatch(completeMasterApp(...params))}
                            sendRequestApprovalLink={(...params) => this.props.dispatch(sendMasterAppLink(...params))}
                            loader={ this.props.loader }
                            displayError={this.props.displayError}
                            whitelabel={{}}
                        />
                    }

                    {
                        this.state.selectedSection === 'account-pending-changes' &&
                        <PendingChanges
                            accountID={accountID}
                        />
                    }

                    {
                        this.state.selectedSection === 'account-invoices' &&
                        <Invoices
                            accountID={accountID}
                        />
                    }

                    {
                        this.state.selectedSection === 'account-division-carry-forward' &&
                        <CarryForward
                            accountID={accountID}
                            isDivision={true}
                        />
                    }

                    {
                        this.state.selectedSection === 'account-aso-reporting' &&
                        <AsoReports
                            accountID={accountID}
                        />
                    }

                    {
                        this.state.selectedSection === 'account-reports' &&
                        <AccountReports
                            accountID={accountID}
                        />
                    }

                    {
                        this.state.selectedSection === 'account-member-booklets' &&
                        <Booklets/>
                    }

                    {
                        this.state.selectedSection === 'account-notes' &&
                        <AccountNotes
                            currentAccountMeta={this.props.currentAccountMeta}
                            classData={this.props.classData}
                            divisions={this.props.divisions}
                            loader={ this.state.loader }
                        />
                    }

                    {
                        this.state.selectedSection === 'account-files' &&
                            <Files
                                isMasterSite={ true }
                                type="accounts"
                                id={ this.props.currentAccountMeta.accountID }
                                parentFolder="files"
                                dispatch={ this.props.dispatch }
                                listFiles={ listFiles }
                                createFolder={ createFolder }
                                uploadFile={ uploadFile }
                                downloadFile={ downloadFile }
                                removeFile={ removeFile }
                                makePrivate={ makePrivate }
                                makePublic={ makePublic }
                                loader={ this.state.loader }
                            />
                    }

                    {
                        this.state.settingsModalOpen &&
                        <PermissionsComponent
                            permission="Account Settings"
                            accessType={this.props.accessType}
                            level="read"
                        >
                            <Settings
                                hideModal={this._toggleSettingsModal}
                                currentAccountMeta={this.props.currentAccountMeta}
                            />
                        </PermissionsComponent>
                    }

                    {
                        this.state.selectedSection === 'account-notifications' &&
                        <PermissionsComponent
                            permission="Account Notifications"
                            accessType={this.props.accessType}
                            level="read"
                        >
                            <Notifications
                                notifications={notifications}
                                meta={currentAccountMeta}
                                notificationsType="account"
                            />
                        </PermissionsComponent>
                    }
                </Content>

                {
                    this.state.showLsaSelections &&
                    <LsaSelectionModal
                        open={this.state.showLsaSelections}
                        isMasterApp={false}
                        getItems={() => this.props.dispatch(getLsaItems(accountID))}
                        onSave={(items) => this.props.dispatch(saveLsaItems(accountID, items))}
                        close={() => this.setState({ showLsaSelections: false })}
                        loader={this.props.loader}
                    />
                }

                <Modal
                    open={this.state.showAllowEnrollmentsDialog}
                    transitionComponent={this._transition}
                    onClose={() => this.setState({ showAllowEnrollmentsDialog: false })}
                    title="Are you sure?"
                    primaryButton={{
                        label: "Enable",
                        onClick: this._enableEnrollments
                    }}
                    loader={this.props.loader}
                >
                    <Text medium>
                        By clicking Enable below, you will be allowing account admins to send enrollments to members.
                        Ensure the Telus data has been loaded and Telus has turned the account on before continuing.
                    </Text>

                    <span id="allow-enrollments" />
                </Modal>

                <Modal
                    open={this.state.showGoLiveDialog}
                    transitionComponent={this._transition}
                    onClose={this._toggleGoLiveDialog}
                    title="Set Effective Date"
                    primaryButton={{
                        label: "Submit",
                        onClick: this._goLive
                    }}
                    loader={this.props.loader}
                >
                    <Text medium>
                        Please provide the date you would like the account to be effective:
                    </Text>

                    <DateInput
                        label="Effective Date"
                        value={this.state.effDate}
                        onBlur={this._setEffectiveDate}
                        error={Boolean(this.state.effectiveDateError)}
                        helperText={this.state.effectiveDateError}
                        fullWidth
                        required
                    />

                    <Text medium margin="10px 0 0 0">
                        By clicking Submit below, the account will be effective on the date provided above. However, we
                        will send a getting started email to the account administrator immediately.
                    </Text>
                </Modal>

                {
                    this.state.showAccountTerminationModal ?
                        <AccountTerminationModal
                            toggleAccountTerminationModal={this._toggleAccountTerminationModal}
                            showAccountTerminationModal={this.state.showAccountTerminationModal}
                        />
                        :
                        null
                }

                <span id="accounts-single"/>
            </ContainerControlSite>
        );
    }
}

AccountDashboard.propTypes = {
    currentAccountMeta: PropTypes.object,
    notifications: PropTypes.array,
    accessType: PropTypes.string
};

/**
 *
 * @param {*} state
 */
const mapStateToProps = (state) => ({
    loader: state.loader,
    bankingDetailsVerifying: state.advisors.bankingDetailsVerifying,
    currentAccountMeta: state.accounts.currentAccount.meta,
    notifications: state.accounts.currentAccount.notifications,
    modularBlueprints: state.accounts.currentAccount.modularBlueprints,
    designs: state.accounts.currentAccount.designs,
    classData: state.accounts.currentAccount.classes,
    divisions: state.accounts.currentAccount.divisions,
    census: state.accounts.currentAccount.employees,
    commissions: state.accounts.currentAccount.commissions,
    specialityProductMetaConfigs: state.accounts.currentAccount.specialityProductMetaConfigs,
    plan: state.accounts.currentAccount.plan,
    accessType: state.master.access,
    advisors: state.advisors,
    representatives: state.accounts.currentAccount.representatives
});

export default connect(mapStateToProps)(AccountDashboard);